/**
 * TutorLiveScreen — la salle du live du tuteur sur le design system (la route `menu` sous son titre `live`,
 * inscrite par `routes.ts`, servie par `tutorSpaceBridge` quand `tutor_ds` est allumé pour ce tuteur et que
 * la salle porte `events-panel`). Le pendant de l'ancien `LiveMainLayout` + `LiveContainerV3` du tuteur.
 *
 * Un fil : chaque zone a son crochet (`tutorLiveContract.ts`), l'écran les compose sur `<TutorLivePage>`.
 * Ce qui ne se voit pas mais doit être monté à la racine — le son de la salle, le sélecteur de partage
 * natif, CallKeep — est rendu à côté de la page, là où rien ne le démonte (tech-derisk, point 2).
 *
 * Les zones se parlent par des refs, pas par l'ordre des appels : la pellicule ouvre la génération du fil
 * et les contrôles du centre, qui lisent tous deux la liste des élèves que rend la pellicule.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { TutorLivePage } from "../../../../../../design-system/tutor"
import { LiveSpeaking } from "../../shared/live/LiveSpeaking"
import { pickLayer } from "./tutorLiveContract"
import { TutorLiveMedia } from "./TutorLiveMedia"
import { type TutorLiveGenerateSource } from "./useTutorLiveCenterActions"
import { useTutorLiveCenter } from "./useTutorLiveCenter"
import { useTutorLiveChrome } from "./useTutorLiveChrome"
import { type TutorLiveGenerateRequest } from "./useTutorLiveChromeChat"
import { type TutorLiveFeed, useTutorLiveFeed } from "./useTutorLiveFeed"
import { useTutorLivePresentation } from "./useTutorLivePresentation"
import { useTutorLiveRoom } from "./useTutorLiveRoom"
import { useTutorLiveSelection } from "./useTutorLiveSelection"
import { useTutorLiveStudents } from "./useTutorLiveStudents"

const NO_PENDING: Record<string, number> = {}
const NO_HAND: string[] = []

export function TutorLiveScreen() {
    const room = useTutorLiveRoom()
    const selection = useTutorLiveSelection()
    const presentation = useTutorLivePresentation(room)

    // Le fil et le centre sont appelés après la pellicule (ils lisent ses élèves) : la pellicule les joint
    // par ces refs, et lit « À traiter » du rendu d'avant (un rendu de retard, rien de visible).
    const feedRef = useRef<TutorLiveFeed | null>(null)
    const openAssessmentsRef = useRef<((studentId: string) => void) | null>(null)
    const openProfileRef = useRef<((studentId: string) => void) | null>(null)
    const [pending, setPending] = useState<Record<string, number>>(NO_PENDING)
    const [handChoice, setHandChoice] = useState<string[]>(NO_HAND)
    const onAddActivity = useCallback((studentId: string) => feedRef.current?.generate({ studentIds: [studentId] }), [])
    const onOpenAssessments = useCallback((studentId: string) => openAssessmentsRef.current?.(studentId), [])
    const onHandChoice = useCallback(
        (studentId: string, choice: "send_sam" | "decline") => feedRef.current?.onHandChoice(studentId, choice),
        []
    )

    const students = useTutorLiveStudents(room, selection, {
        onAddActivity,
        onOpenAssessments,
        pendingOf: pending,
        handChoiceOf: handChoice,
        onHandChoice,
    })
    // La mosaïque et le chat vivent dans la barre, appelée après le fil : le fil les lit au rendu d'après (rien de
    // visible). Le centre aussi : « /similaire » du fil lit son activité ouverte par une ref.
    // La salle s'ouvre sur la mosaïque (`useTutorLiveChrome`) : le fil le sait dès son premier rendu.
    const [mosaicShown, setMosaicShown] = useState(true)
    const [chatOpen, setChatOpen] = useState(false)
    const centerRef = useRef<{ similarSource: () => TutorLiveGenerateSource | null; focus: unknown } | null>(null)
    const withPreview = useCallback((source: TutorLiveGenerateSource): TutorLiveGenerateSource => {
        const focus = centerRef.current?.focus as
            | { activityId?: string; statement?: { text?: string } | null; work?: { images?: string[] } | null }
            | null
            | undefined
        if (focus?.activityId !== source.activityId) return source
        return { ...source, preview: { statement: focus.statement?.text, imageUri: focus.work?.images?.[0] } }
    }, [])
    const similarSource = useCallback(
        (studentId: string) => {
            const source = centerRef.current?.similarSource() ?? null
            return source != null && source.studentId === studentId ? withPreview(source) : null
        },
        [withPreview]
    )
    const feed = useTutorLiveFeed(room, selection, students.students, {
        mosaic: mosaicShown,
        presenting: presentation.on || presentation.pending,
        chatOpen,
        centerOnly: presentation.centerOnly,
        similarSource,
    })
    feedRef.current = feed
    useEffect(() => setPending(feed.pending), [feed.pending])
    useEffect(() => setHandChoice(feed.handChoice), [feed.handChoice])

    const generate = feed.generate
    // « Tous en Dialogue » sous la tête pendant une présentation : le geste même de « Plus » (la barre est
    // appelée après le centre, d'où la ref).
    const allDialogueRef = useRef<(() => void) | null>(null)
    const onAllDialogue = useCallback(() => allDialogueRef.current?.(), [])
    const center = useTutorLiveCenter(room, selection, presentation, students.students, {
        onAllDialogue,
        loadProfile: students.zone.onOpenProfile,
    })
    openAssessmentsRef.current = center.openAssessments
    openProfileRef.current = center.openProfile
    centerRef.current = { similarSource: center.similarSource, focus: center.zone.focus }

    // Une pièce du chat transformée en activité : sa pièce le dit dans le brouillon, avec le chapitre en cours.
    const onGenerate = useCallback(
        (request: TutorLiveGenerateRequest) =>
            generate(request.attachment != null ? { ...request, origin: { kind: "chat", at: request.at } } : request),
        [generate]
    )
    const chrome = useTutorLiveChrome(room, selection, presentation, {
        generate: onGenerate,
        refresh: feed.refresh,
    })
    allDialogueRef.current = () => chrome.overlays.more.onAction("all_dialogue")

    // « + Activité » de la barre : la colonne de la session revient si elle était repliée, puis le champ ressort,
    // pré-rempli (il prend le focus au rendu qui le monte). La fiche ouverte, elle, reste : la page ne la referme
    // qu'au téléphone, où elle couvre l'écran.
    const { addActivity } = feed
    const panes = chrome.panes
    const bar = {
        ...chrome.bar,
        onAddActivity: () => {
            if (panes?.feedCollapsed === true) panes.onToggleFeed()
            addActivity()
        },
    }

    // Une notification de message de cette séance, touchée : le chat s'ouvre sur son fil, une fois par toucher.
    const { openChat } = selection
    const chatSeq = room.chatRequest?.seq
    const chatChannel = room.chatRequest?.channel
    useEffect(() => {
        if (chatSeq != null && chatChannel != null) openChat(chatChannel)
    }, [chatSeq, chatChannel, openChat])

    // Toucher dans la mosaïque l'élève déjà regardé la referme aussi (la barre la referme quand il change).
    const mosaic = chrome.bar.mosaic

    // La roue d'une carte en vol (le fil la rend à part de sa zone) ; en mosaïque, le fil de tout le groupe :
    // la mosaïque n'est jamais filtrée.
    const mosaicOn = mosaic?.on === true
    useEffect(() => setMosaicShown(mosaicOn), [mosaicOn])
    const chatShown = chrome.overlays.chat != null
    useEffect(() => setChatOpen(chatShown), [chatShown])
    const feedZone = useMemo(
        () => ({ ...feed.zone, pending: feed.feedPending ?? undefined }),
        [feed.zone, feed.feedPending]
    )
    const selectStudent = students.zone.onSelect
    const openActivity = students.zone.onOpenActivity
    const openThread = center.openThread
    const watchStudent = feed.watchStudent
    const openHelperSummary = feed.openHelperSummary
    const studentsZone = useMemo(
        () => ({
            ...students.zone,
            // Le fil de chaque élève, sur sa tuile : le fil le calcule, après la pellicule.
            list: students.zone.list.map((student) => ({ ...student, thread: feed.threads[student.id] ?? null })),
            // Toucher un élève de la mosaïque est un geste : il la referme sur lui, qu'il soit déjà regardé, un autre, ou
            // que la salle n'en regarde encore aucun. La barre, elle, ne suit que les changements d'élève regardé.
            onSelect: (studentId: string) => {
                if (mosaic?.on === true) mosaic.onToggle()
                watchStudent()
                selectStudent(studentId)
            },
            // Ouvrir une activité depuis le fil d'une tuile est aussi regarder l'élève : la mosaïque se referme sur
            // l'activité, sinon elle l'ouvrirait dessous.
            onOpenActivity: (studentId: string, activityId: string) => {
                if (mosaic?.on === true) mosaic.onToggle()
                watchStudent()
                openActivity(studentId, activityId)
            },
            // Le fil d'une tuile ouvre l'onglet « Fil » de l'élève : depuis la mosaïque, c'est passer à sa vue. Celui
            // d'un élève que le champion aide ouvre son résumé, le geste de la carte « <champion> aide … » sortie de « À traiter ».
            onOpenThread:
                openThread != null
                    ? (studentId: string, eventId?: string) => {
                          if (openHelperSummary(studentId)) return
                          if (mosaic?.on === true) mosaic.onToggle()
                          watchStudent()
                          openThread(studentId, eventId)
                      }
                    : undefined,
            // La fiche est un onglet du centre (live du 04/11) : le centre la charge et la montre.
            onOpenProfile: (studentId: string) => openProfileRef.current?.(studentId),
        }),
        [students.zone, feed.threads, mosaic, selectStudent, openActivity, openThread, watchStudent, openHelperSummary]
    )

    // Une boîte ou une snackbar ouverte sans geste se pose par-dessus le centre : recadrée ou non, une
    // présentation l'emporterait chez les élèves. Elle attend sa fin, et déjà son départ (le sélecteur ouvert).
    const layer = pickLayer([room.layer, students.layer, center.layer, feed.layer, chrome.layer], {
        presenting: presentation.on || presentation.pending,
    })

    return (
        <>
            {room.mounts}
            <TutorLiveMedia telemetry={room.telemetry} covered={layer.dialog != null} />
            <LiveSpeaking>
                <TutorLivePage
                    me={room.me}
                    coTutors={room.coTutors}
                    roomKind={room.roomKind}
                    startedAt={feed.sessionStartedAt}
                    notice={room.notice}
                    onRetryConnection={room.onRetryConnection}
                    onCancelConnection={room.onCancelConnection}
                    students={studentsZone}
                    profile={students.profile}
                    center={{
                        ...center.zone,
                        replay: feed.replay != null ? { ...feed.replay, focus: center.threadFocus } : feed.replay,
                    }}
                    feed={feedZone}
                    bar={bar}
                    panes={chrome.panes}
                    centerOnly={presentation.centerOnly}
                    overlays={{
                        ...chrome.overlays,
                        samSummary: feed.samSummary,
                        quizSignal: center.quizSignal,
                        dialog: layer.dialog,
                        snackbar: layer.snackbar,
                        sheet: layer.sheet,
                    }}
                    renderVideo={room.renderVideo}
                    helper={room.helper}
                />
            </LiveSpeaking>
        </>
    )
}
